<template>
  <section class="relative isolate min-h-[calc(100vh-8rem)] overflow-hidden bg-[#fdf7f0] py-8">
    <div class="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div class="grid h-full w-full grid-cols-3 gap-3 px-3 opacity-95 sm:grid-cols-4 sm:gap-4 sm:px-4 lg:grid-cols-6">
        <div
          v-for="(column, columnIndex) in imageColumns"
          :key="columnIndex"
          class="auth-masonry-column"
          :class="{ 'hidden lg:grid': columnIndex > 3 }"
        >
          <img
            v-for="card in column"
            :key="card.src"
            class="h-full min-h-[9rem] w-full rounded-2xl object-cover shadow-xl shadow-stone-900/10"
            :src="card.src"
            :alt="card.alt"
            loading="lazy"
          />
        </div>
      </div>
      <div class="absolute inset-0 bg-gradient-to-b from-[#fdf7f0]/20 via-[#fdf7f0]/55 to-[#fdf7f0]" />
      <div class="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#fdf7f0] to-transparent" />
    </div>

    <div class="mx-auto flex min-h-[calc(100vh-12rem)] w-full max-w-6xl items-center justify-center px-4 sm:px-6 lg:px-8">
      <div class="grid w-full max-w-5xl items-center gap-8 lg:grid-cols-[1fr_27rem]">
        <div class="hidden max-w-xl text-slate-950 lg:block">
          <p class="mb-4 text-sm font-black uppercase tracking-[0.22em] text-rose-700">Account recovery</p>
          <h1 class="m-0 text-5xl font-black leading-[0.95]">
            Get back to the flyers you saved and shared.
          </h1>
          <p class="mt-5 max-w-lg text-lg leading-8 text-slate-700">
            Request a one-time code, verify your account, and set a stronger password before returning
            to your local flyer feed.
          </p>
          <div class="mt-8 grid max-w-lg grid-cols-3 gap-3">
            <div class="rounded-lg bg-white/80 p-4 shadow-lg shadow-stone-900/10 backdrop-blur">
              <strong class="block text-2xl font-black text-rose-700">OTP</strong>
              <span class="text-sm font-bold text-slate-600">verification</span>
            </div>
            <div class="rounded-lg bg-white/80 p-4 shadow-lg shadow-stone-900/10 backdrop-blur">
              <strong class="block text-2xl font-black text-blue-700">Strong</strong>
              <span class="text-sm font-bold text-slate-600">passwords</span>
            </div>
            <div class="rounded-lg bg-white/80 p-4 shadow-lg shadow-stone-900/10 backdrop-blur">
              <strong class="block text-2xl font-black text-emerald-700">Secure</strong>
              <span class="text-sm font-bold text-slate-600">access</span>
            </div>
          </div>
        </div>

        <form
          class="mx-auto w-full max-w-[27rem] rounded-[2rem] border border-white/80 bg-white/95 p-6 shadow-2xl shadow-stone-900/20 backdrop-blur sm:p-8"
          @submit.prevent="submitRecovery"
        >
          <div class="mb-7 text-center">
            <img class="mx-auto mb-4 h-12 w-auto" src="/logo.svg" alt="NetFlyr" />
            <h2 class="m-0 text-3xl font-black leading-tight text-slate-950">Reset password</h2>
            <p class="mx-auto mt-2 max-w-xs text-sm leading-6 text-slate-600">
              {{ isOtpStep ? 'Enter the OTP and set your new password.' : 'Enter your email to receive a password reset OTP.' }}
            </p>
          </div>

          <div class="mb-5 grid grid-cols-2 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-1 text-xs font-black">
            <span class="rounded-xl px-3 py-2 text-center" :class="!isOtpStep ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'">
              Email
            </span>
            <span class="rounded-xl px-3 py-2 text-center" :class="isOtpStep ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'">
              Verify
            </span>
          </div>

          <div class="grid gap-4">
            <label class="grid gap-1.5 text-sm font-extrabold text-slate-700">
              <span>Email address</span>
              <input
                v-model="email"
                class="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-rose-500 focus:ring-4 focus:ring-rose-100 disabled:bg-slate-50 disabled:text-slate-500"
                type="email"
                autocomplete="email"
                placeholder="you@example.com"
                :disabled="isOtpStep"
                required
              />
            </label>

            <template v-if="isOtpStep">
              <label class="grid gap-1.5 text-sm font-extrabold text-slate-700">
                <span>OTP</span>
                <input
                  v-model="otp"
                  class="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-center text-lg font-black tracking-[0.32em] text-slate-950 outline-none transition placeholder:tracking-normal placeholder:text-slate-400 focus:border-rose-500 focus:ring-4 focus:ring-rose-100"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  placeholder="Enter OTP"
                  required
                />
              </label>

              <PasswordInput
                v-model="password"
                label="New password"
                placeholder="Create a new password"
                :visible="showPassword"
                generator
                @toggle="showPassword = !showPassword"
                @generate="generatePassword"
              />

              <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div class="flex items-center justify-between gap-3 text-xs font-black uppercase tracking-[0.16em] text-slate-500">
                  <span>Password strength</span>
                  <span :class="strengthLabelClass">{{ strengthLabel }}</span>
                </div>
                <div class="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                  <div class="h-full rounded-full transition-all" :class="strengthBarClass" :style="{ width: strengthPercent + '%' }" />
                </div>
                <ul class="mt-3 grid gap-2 text-xs font-bold leading-5 text-slate-600">
                  <li
                    v-for="item in passwordChecklist"
                    :key="item.label"
                    class="flex items-center gap-2"
                    :class="item.valid ? 'text-emerald-700' : 'text-slate-500'"
                  >
                    <span
                      class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[0.65rem] font-black"
                      :class="item.valid ? 'bg-emerald-100 text-emerald-700' : 'bg-white text-slate-400'"
                    >
                      {{ item.valid ? 'OK' : '-' }}
                    </span>
                    {{ item.label }}
                  </li>
                </ul>
              </div>

              <PasswordInput
                v-model="confirmPassword"
                label="Confirm password"
                placeholder="Re-enter new password"
                :visible="showConfirmPassword"
                :error="Boolean(confirmPassword && !passwordsMatch)"
                @toggle="showConfirmPassword = !showConfirmPassword"
              />

              <span
                v-if="confirmPassword"
                class="-mt-2 text-xs font-bold"
                :class="passwordsMatch ? 'text-emerald-700' : 'text-red-600'"
              >
                {{ passwordsMatch ? 'Passwords match.' : 'Passwords do not match yet.' }}
              </span>
            </template>
          </div>

          <div
            v-if="formMessage"
            class="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold leading-6 text-emerald-800"
          >
            {{ formMessage }}
          </div>

          <div
            v-if="formError"
            class="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold leading-6 text-red-700"
          >
            {{ formError }}
          </div>

          <FlyerButton
            class="mt-5 w-full rounded-full bg-blue-600 hover:bg-blue-700 focus:ring-blue-200"
            type="submit"
            :disabled="isSubmitting"
          >
            {{ submitLabel }}
          </FlyerButton>

          <button
            v-if="isOtpStep"
            class="mt-3 inline-flex w-full min-h-11 items-center justify-center rounded-full border border-slate-200 bg-white px-5 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
            type="button"
            :disabled="isSubmitting"
            @click="resetEmailStep"
          >
            Use a different email
          </button>

          <p class="mt-6 text-center text-sm font-bold leading-6 text-slate-600">
            Remembered your password?
            <router-link class="font-black text-blue-700 hover:text-blue-800" to="/login">
              Back to login
            </router-link>
          </p>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, defineComponent, h, ref } from 'vue'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import api from '@/services/api'
import FlyerButton from '@/components/common/FlyerButton.vue'

const router = useRouter()
const email = ref('')
const otp = ref('')
const password = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const isOtpStep = ref(false)
const isSubmitting = ref(false)
const formError = ref('')
const formMessage = ref('')

const passwordChecks = computed(() => ({
  length: password.value.length >= 12,
  uppercase: /[A-Z]/.test(password.value),
  lowercase: /[a-z]/.test(password.value),
  number: /\d/.test(password.value),
  symbol: /[^A-Za-z0-9]/.test(password.value)
}))

const passwordChecklist = computed(() => [
  { label: 'Use at least 12 characters', valid: passwordChecks.value.length },
  { label: 'Include uppercase and lowercase letters', valid: passwordChecks.value.uppercase && passwordChecks.value.lowercase },
  { label: 'Add at least one number', valid: passwordChecks.value.number },
  { label: 'Add at least one symbol', valid: passwordChecks.value.symbol }
])

const strengthScore = computed(() => Object.values(passwordChecks.value).filter(Boolean).length)
const strengthPercent = computed(() => (strengthScore.value / 5) * 100)
const passwordsMatch = computed(() => Boolean(password.value && confirmPassword.value && password.value === confirmPassword.value))
const submitLabel = computed(() => {
  if (isSubmitting.value) return isOtpStep.value ? 'Updating password...' : 'Sending OTP...'
  return isOtpStep.value ? 'Verify and update password' : 'Send OTP'
})

const strengthLabel = computed(() => {
  if (!password.value) return 'Start'
  if (strengthScore.value <= 2) return 'Weak'
  if (strengthScore.value <= 4) return 'Good'
  return 'Strong'
})

const strengthBarClass = computed(() => {
  if (!password.value) return 'bg-slate-300'
  if (strengthScore.value <= 2) return 'bg-red-500'
  if (strengthScore.value <= 4) return 'bg-amber-500'
  return 'bg-emerald-500'
})

const strengthLabelClass = computed(() => {
  if (!password.value) return 'text-slate-500'
  if (strengthScore.value <= 2) return 'text-red-600'
  if (strengthScore.value <= 4) return 'text-amber-600'
  return 'text-emerald-700'
})

const imageColumns = [
  [
    { src: 'https://dummyimage.com/360x520/f43f5e/ffffff&text=Saved+Events', alt: 'Colorful NetFlyr placeholder for saved events' },
    { src: 'https://dummyimage.com/360x430/0ea5e9/ffffff&text=Local+Music', alt: 'Colorful NetFlyr placeholder for local music flyers' }
  ],
  [
    { src: 'https://dummyimage.com/360x420/f59e0b/ffffff&text=Food+Offers', alt: 'Colorful NetFlyr placeholder for food offer flyers' },
    { src: 'https://dummyimage.com/360x560/16a34a/ffffff&text=Service+Deals', alt: 'Colorful NetFlyr placeholder for service deal flyers' }
  ],
  [
    { src: 'https://dummyimage.com/360x590/8b5cf6/ffffff&text=Workshops', alt: 'Colorful NetFlyr placeholder for workshop flyers' },
    { src: 'https://dummyimage.com/360x400/ec4899/ffffff&text=Saved+Sales', alt: 'Colorful NetFlyr placeholder for saved sale flyers' }
  ],
  [
    { src: 'https://dummyimage.com/360x470/14b8a6/ffffff&text=Meetups', alt: 'Colorful NetFlyr placeholder for meetup flyers' },
    { src: 'https://dummyimage.com/360x540/ef4444/ffffff&text=Openings', alt: 'Colorful NetFlyr placeholder for opening flyers' }
  ],
  [
    { src: 'https://dummyimage.com/360x530/2563eb/ffffff&text=Classes', alt: 'Colorful NetFlyr placeholder for class flyers' },
    { src: 'https://dummyimage.com/360x410/a855f7/ffffff&text=Markets', alt: 'Colorful NetFlyr placeholder for market flyers' }
  ],
  [
    { src: 'https://dummyimage.com/360x430/f97316/ffffff&text=Jobs', alt: 'Colorful NetFlyr placeholder for job flyers' },
    { src: 'https://dummyimage.com/360x570/0891b2/ffffff&text=Updates', alt: 'Colorful NetFlyr placeholder for local updates' }
  ]
]

const getApiMessage = (payload, fallback) => {
  const message = payload?.message || payload?.msg || payload?.data?.message || payload?.data?.msg
  return typeof message === 'string' && message.trim() ? message : fallback
}

const apiCallSucceeded = (payload) => payload?.status === undefined || payload?.status === 'ok'

const generatePassword = () => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lower = 'abcdefghijkmnopqrstuvwxyz'
  const numbers = '23456789'
  const symbols = '!@#$%^&*?'
  const groups = [upper, lower, numbers, symbols]
  const allCharacters = groups.join('')
  const generated = [
    ...groups.map((group) => group[Math.floor(Math.random() * group.length)]),
    ...Array.from({ length: 12 }, () => allCharacters[Math.floor(Math.random() * allCharacters.length)])
  ]
    .sort(() => Math.random() - 0.5)
    .join('')

  password.value = generated
  confirmPassword.value = generated
  showPassword.value = true
  showConfirmPassword.value = true
  formError.value = ''
}

const sendOtp = async () => {
  const trimmedEmail = email.value.trim()

  if (!trimmedEmail) {
    formError.value = 'Enter the email connected to your account.'
    return
  }

  isSubmitting.value = true

  try {
    const { data } = await api.loginReset({ email: trimmedEmail, type: 'flyer' })

    if (!apiCallSucceeded(data)) {
      formError.value = getApiMessage(data, 'Could not send reset OTP.')
      return
    }

    isOtpStep.value = true
    formMessage.value = getApiMessage(data, `OTP sent to ${trimmedEmail}.`)
  } catch (error) {
    formError.value = getApiMessage(error?.response?.data, error?.message || 'Could not send reset OTP.')
  } finally {
    isSubmitting.value = false
  }
}

const verifyAndUpdatePassword = async () => {
  const trimmedEmail = email.value.trim()
  const trimmedOtp = otp.value.trim()

  if (!trimmedOtp || !password.value || !confirmPassword.value) {
    formError.value = 'Fill OTP, password, and confirm password.'
    return
  }

  if (!passwordsMatch.value) {
    formError.value = 'Confirm password must match your new password.'
    return
  }

  if (strengthScore.value < 5) {
    formError.value = 'Please use a strong password before updating.'
    return
  }

  isSubmitting.value = true

  try {
    const { data } = await api.loginResetVerify({
      email: trimmedEmail,
      otp: trimmedOtp,
      password: password.value,
      confirm_password: confirmPassword.value,
      newpwd: password.value
    })

    if (!apiCallSucceeded(data)) {
      formError.value = getApiMessage(data, 'Password could not be reset.')
      return
    }

    await Swal.fire({
      icon: 'success',
      title: 'Password updated',
      text: getApiMessage(data, 'Your password has been reset.'),
      confirmButtonColor: '#e11d48'
    })

    router.push('/login')
  } catch (error) {
    formError.value = getApiMessage(error?.response?.data, error?.message || 'Password could not be reset.')
  } finally {
    isSubmitting.value = false
  }
}

const submitRecovery = () => {
  formError.value = ''
  formMessage.value = ''

  if (isOtpStep.value) {
    verifyAndUpdatePassword()
    return
  }

  sendOtp()
}

const resetEmailStep = () => {
  isOtpStep.value = false
  otp.value = ''
  password.value = ''
  confirmPassword.value = ''
  formError.value = ''
  formMessage.value = ''
  showPassword.value = false
  showConfirmPassword.value = false
}

const PasswordInput = defineComponent({
  props: {
    modelValue: { type: String, default: '' },
    label: { type: String, required: true },
    placeholder: { type: String, default: '' },
    visible: { type: Boolean, default: false },
    generator: { type: Boolean, default: false },
    error: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'toggle', 'generate'],
  setup(props, { emit }) {
    return () =>
      h('label', { class: 'grid gap-1.5 text-sm font-extrabold text-slate-700' }, [
        h('span', props.label),
        h('div', { class: 'relative' }, [
          h('input', {
            class: [
              'min-h-12 w-full rounded-2xl border bg-white px-4 py-3 text-slate-950 outline-none transition placeholder:text-slate-400 focus:ring-4',
              props.generator ? 'pr-20' : 'pr-12',
              props.error ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : 'border-slate-300 focus:border-rose-500 focus:ring-rose-100'
            ],
            value: props.modelValue,
            type: props.visible ? 'text' : 'password',
            autocomplete: 'new-password',
            placeholder: props.placeholder,
            required: true,
            onInput: (event) => emit('update:modelValue', event.target.value)
          }),
          props.generator
            ? h('button', {
                class: 'absolute right-11 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-4 focus:ring-rose-100',
                type: 'button',
                'aria-label': 'Generate strong password',
                onClick: () => emit('generate')
              }, [h('i', { class: 'ri-magic-line text-xl', 'aria-hidden': 'true' })])
            : null,
          h('button', {
            class: 'absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-4 focus:ring-rose-100',
            type: 'button',
            'aria-label': props.visible ? 'Hide password' : 'Show password',
            'aria-pressed': props.visible,
            onClick: () => emit('toggle')
          }, [h('i', { class: props.visible ? 'ri-eye-off-line text-xl' : 'ri-eye-line text-xl', 'aria-hidden': 'true' })])
        ])
      ])
  }
})
</script>

<style scoped>
.auth-masonry-column {
  display: grid;
  min-height: 100%;
  grid-template-rows: 1.1fr 0.9fr;
  gap: 0.75rem;
}

.auth-masonry-column:nth-child(even) {
  transform: translateY(2rem);
}

.auth-masonry-column:nth-child(3n) {
  transform: translateY(-1.5rem);
}

@media (min-width: 640px) {
  .auth-masonry-column {
    gap: 1rem;
  }
}

@media (max-width: 1023px) {
  .auth-masonry-column:nth-child(even),
  .auth-masonry-column:nth-child(3n) {
    transform: none;
  }
}
</style>
